import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { getAssessments, sendInvitation, resendInvitation } from 'services/vendor-assessment-service';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

export const AssessmentList = ({ tenant }) => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery(
    ['assessments', tenant, statusFilter],
    () => getAssessments(tenant, { status: statusFilter || undefined }),
    { enabled: !!tenant, keepPreviousData: true }
  );

  // Handle API response with success field
  const assessments = (data?.data?.success && data?.data?.data) || [];

  const handleSendInvitation = async (id, vendorName) => {
    try {
      await sendInvitation(id);
      queryClient.invalidateQueries(['assessments']);
      SwalReact.fire({
        icon: 'success',
        title: t('gap-analysis:assessment.list.swal.invitationSentTitle'),
        html: `<p>${t('gap-analysis:assessment.list.swal.invitationSentHtml', { vendorName })}</p>`,
        timer: 3000
      });
    } catch (error) {
      SwalReact.fire({
        icon: 'error',
        title: t('gap-analysis:assessment.list.swal.errorTitle'),
        text: error.response?.data?.error || t('gap-analysis:assessment.list.swal.errorSend')
      });
    }
  };

  const handleResendInvitation = async (id, vendorName) => {
    try {
      await resendInvitation(id);
      queryClient.invalidateQueries(['assessments']);
      SwalReact.fire({
        icon: 'success',
        title: t('gap-analysis:assessment.list.swal.emailSentTitle'),
        text: t('gap-analysis:assessment.list.swal.emailSentText', { vendorName }),
        timer: 2000
      });
    } catch (error) {
      SwalReact.fire({
        icon: 'error',
        title: t('gap-analysis:assessment.list.swal.errorTitle'),
        text: error.response?.data?.error || t('gap-analysis:assessment.list.swal.errorResend')
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'default',
      'Pending Vendor Response': 'warning',
      'Under Review': 'primary',
      'Pending Executive Approval': 'warning',
      'Approved': 'success',
      'Rejected': 'error',
      'Contractual Review': 'secondary'
    };
    return colors[status] || 'default';
  };

  const getTrustScoreColor = (score) => {
    if (!score) return 'default';
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('gap-analysis:assessment.list.filterByStatus')}</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label={t('gap-analysis:assessment.list.filterByStatus')}
          >
            <MenuItem value="">{t('gap-analysis:assessment.list.allStatuses')}</MenuItem>
            <MenuItem value="Draft">{t('gap-analysis:assessment.list.statuses.draft')}</MenuItem>
            <MenuItem value="Pending Vendor Response">{t('gap-analysis:assessment.list.statuses.pendingVendorResponse')}</MenuItem>
            <MenuItem value="Under Review">{t('gap-analysis:assessment.list.statuses.underReview')}</MenuItem>
            <MenuItem value="Pending Executive Approval">{t('gap-analysis:assessment.list.statuses.pendingExecutiveApproval')}</MenuItem>
            <MenuItem value="Approved">{t('gap-analysis:assessment.list.statuses.approved')}</MenuItem>
            <MenuItem value="Rejected">{t('gap-analysis:assessment.list.statuses.rejected')}</MenuItem>
            <MenuItem value="Contractual Review">{t('gap-analysis:assessment.list.statuses.contractualReview')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('gap-analysis:assessment.list.table.vendor')}</TableCell>
              <TableCell>{t('gap-analysis:assessment.list.table.framework')}</TableCell>
              <TableCell>{t('common:labels.status')}</TableCell>
              <TableCell>{t('gap-analysis:assessment.list.table.trustScore')}</TableCell>
              <TableCell>{t('gap-analysis:assessment.list.table.createdDate')}</TableCell>
              <TableCell align="right">{t('common:table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assessments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">
                    {t('gap-analysis:assessment.list.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              assessments.map((assessment) => (
                <TableRow key={assessment._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {assessment.vendorName}
                    </Typography>
                  </TableCell>
                  <TableCell>{assessment.frameworkName}</TableCell>
                  <TableCell>
                    <Chip
                      label={assessment.status}
                      color={getStatusColor(assessment.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {assessment.trustScore ? (
                      <Chip
                        label={`${assessment.trustScore}%`}
                        color={getTrustScoreColor(assessment.trustScore)}
                        size="small"
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        {t('gap-analysis:assessment.list.notScoredYet')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDateTimeToDateTimeBR(assessment.createdAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('gap-analysis:assessment.list.viewDetails')}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/vendor-assessments/${assessment._id}`)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {assessment.status === 'Draft' && (
                      <Tooltip title={t('gap-analysis:assessment.list.sendInvitation')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSendInvitation(assessment._id, assessment.vendorName)}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {assessment.status === 'Pending Vendor Response' && (
                      <Tooltip title={t('gap-analysis:assessment.list.resendInvitation')}>
                        <IconButton
                          size="small"
                          onClick={() => handleResendInvitation(assessment._id, assessment.vendorName)}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2" color="textSecondary">
          {t('gap-analysis:assessment.list.showing', { count: assessments.length })}
        </Typography>
      </Box>
    </Box>
  );
};
